import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { segmentos, segmento, tarjeta } from '../../components/ui/estilos';
import { PERIODOS, usePeriodo } from './periodo';

type P = ReturnType<typeof usePeriodo>;
const fechaCampo: React.CSSProperties = { height: 40, padding: '0 10px', borderRadius: 8, border: '1px solid var(--line-strong)', fontSize: 14, background: 'var(--surface)', color: 'var(--ink)' };

// Sin `p` (p. ej. Alertas) no muestra el selector de periodo.
export function Encabezado({ titulo, p, volver = true, volverA = { to: '/dashboard', texto: 'Inicio' }, sinPersonalizado, extra }: { titulo: string; p?: P; volver?: boolean; volverA?: { to: string; texto: string }; sinPersonalizado?: boolean; extra?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
      {volver && <Link to={volverA.to} style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textDecoration: 'none' }}>← {volverA.texto}</Link>}
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{titulo}</h1>
      {p && <div role="tablist" aria-label="Periodo" style={segmentos}>
        {PERIODOS.filter(([k]) => !(sinPersonalizado && k === 'personalizado')).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={p.periodo === k} onClick={() => p.setPeriodo(k)} style={{ ...segmento(p.periodo === k), height: 38, fontSize: 14, padding: '0 14px' }}>{l}</button>
        ))}
      </div>}
      {p?.periodo === 'personalizado' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="date" value={p.desde} max={p.hasta} onChange={e => p.setDesde(e.target.value)} aria-label="Desde" style={fechaCampo} />
          <span style={{ color: 'var(--muted)', fontSize: 14 }}>a</span>
          <input type="date" value={p.hasta} onChange={e => p.setHasta(e.target.value)} aria-label="Hasta" style={fechaCampo} />
        </div>
      )}
      <div style={{ flex: 1 }} />
      {extra}
    </div>
  );
}

export function Kpis({ items, onClick }: { items: { label: string; valor: string; delta?: { texto: string; color: string }; to?: string }[]; onClick?: (to: string) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(190px, 100%), 1fr))', gap: 14 }}>
      {items.map(k => {
        const contenido = <>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.02em', color: 'var(--muted)', textTransform: 'uppercase' }}>{k.label}</span>
          <span style={{ fontSize: 26, fontWeight: 700 }}>{k.valor}</span>
          {k.delta && <span style={{ fontSize: 13, fontWeight: 700, color: k.delta.color }}>{k.delta.texto}</span>}
        </>;
        const estilo: React.CSSProperties = { ...tarjeta, borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left', color: 'var(--ink)' };
        return k.to && onClick
          ? <button key={k.label} onClick={() => onClick(k.to!)} style={{ ...estilo, cursor: 'pointer' }}>{contenido}</button>
          : <div key={k.label} style={estilo}>{contenido}</div>;
      })}
    </div>
  );
}

export function Tarjeta({ titulo, extra, children, gap = 10 }: { titulo: string; extra?: ReactNode; children: ReactNode; gap?: number }) {
  return (
    <section style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{titulo}</h2>{extra}
      </div>
      {children}
    </section>
  );
}

export function VerMas({ to, texto = 'Ver más →' }: { to: string; texto?: string }) {
  return <Link to={to} style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-texto)', textDecoration: 'none', whiteSpace: 'nowrap' }}>{texto}</Link>;
}

// Barras verticales; la destacada va en color de marca.
export function Barras({ datos, alto = 160, formato, sinValores }: {
  datos: { label: string; valor: number; destacada?: boolean }[]; alto?: number; formato: (n: number) => string; sinValores?: boolean;
}) {
  const max = Math.max(1, ...datos.map(d => d.valor));
  const fijos = datos.length <= 7;
  if (!datos.length) return <div style={{ color: 'var(--muted)', fontSize: 15, padding: '20px 0' }}>Sin ventas en este periodo.</div>;
  return (
    <div role="img" aria-label={datos.map(d => `${d.label}: ${formato(d.valor)}`).join(', ')} style={{ display: 'flex', alignItems: 'flex-end', gap: datos.length > 14 ? 4 : 10, height: alto }}>
      {datos.map((d, i) => (
        <div key={i} className="barra" tabIndex={0} aria-label={`${d.label}: ${formato(d.valor)}`} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
          {/* Con pocas barras el valor va fijo arriba; con muchas se encimaban: sale al pasar el cursor. */}
          {!sinValores && fijos && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{formato(d.valor)}</span>}
          <div className="barra-bar" style={{ position: 'relative', width: '100%', maxWidth: 44, borderRadius: '6px 6px 2px 2px', background: d.destacada ? 'var(--brand-texto)' : 'var(--well)', height: `${Math.max(2, d.valor / max * 100)}%` }}>
            {!fijos && <span className="barra-tip" role="tooltip">{d.label} · {formato(d.valor)}</span>}
          </div>
          <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

export function Progreso({ label, valor, pct }: { label: string; valor: string; pct: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}><span>{label}</span><span style={{ fontWeight: 700 }}>{valor}</span></div>
      <div style={{ height: 8, borderRadius: 999, background: 'var(--well)' }} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div style={{ height: '100%', borderRadius: 999, background: 'var(--brand-texto)', width: `${Math.min(100, Math.max(0, pct))}%` }} />
      </div>
    </div>
  );
}

// Tabla ligera en rejilla (encabezado gris + filas con divisor).
export function Tabla({ columnas, filas, vacio = 'Sin datos en este periodo.' }: { columnas: { t: string; w: string; der?: boolean }[]; filas: ReactNode[][]; vacio?: string }) {
  const grid = columnas.map(c => c.w).join(' ');
  // Ancho mínimo = columnas fijas + holgura para la flexible: en celular la tabla se desliza de lado.
  const minimo = columnas.reduce((s, c) => s + (parseInt(c.w) || 0), 0) + 8 * columnas.length + 120;
  return (
    <div style={{ overflowX: 'auto' }}><div style={{ minWidth: minimo }}>
      <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '8px 4px', fontSize: 12, fontWeight: 700, color: 'var(--muted)', borderBottom: '1px solid var(--line)' }}>
        {columnas.map(c => <span key={c.t} style={{ textAlign: c.der ? 'right' : 'left' }}>{c.t}</span>)}
      </div>
      {!filas.length && <div style={{ padding: '14px 4px', color: 'var(--muted)', fontSize: 15 }}>{vacio}</div>}
      {filas.map((f, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '8px 4px', borderBottom: '1px solid var(--line-2)', fontSize: 15, alignItems: 'center' }}>
          {f.map((celda, j) => <span key={j} style={{ textAlign: columnas[j].der ? 'right' : 'left', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{celda}</span>)}
        </div>
      ))}
    </div></div>
  );
}
